import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import './EmailTemplates.css';
import TemplateItem from './TemplateItem';
import PreviewModule from './PreviewModule/PreviewModule';

// Import template JavaScript files
import normalTemplate1 from '../Assets/Templates/Normal/normal1';
import normalTemplate2 from '../Assets/Templates/Normal/normal2';
import normalTemplate3 from '../Assets/Templates/Normal/normal3';
import ampscriptTemplate1 from '../Assets/Templates/Amp/Amp1';
import ampscriptTemplate2 from '../Assets/Templates/Amp/Amp2';
import ampscriptTemplate3 from '../Assets/Templates/Amp/Amp3';
import interactiveTemplate1 from '../Assets/Templates/Interactive/Interactive1';
import interactiveTemplate2 from '../Assets/Templates/Interactive/Interactive2';

const EmailTemplates = () => {
  const [activeTab, setActiveTab] = useState('normal'); // Default tab: Normal Templates
  const [selectedTemplate, setSelectedTemplate] = useState(null); // State for selected template

  // Map imported HTML strings to template data with type
  const templates = {
    normal: [
      {
        id: 1,
        title: 'Normal Template 1',
        description: 'A clean and simple HTML email template for newsletters.',
        html: normalTemplate1,
        type: 'normal', // Add type
      },
      {
        id: 2,
        title: 'Normal Template 2',
        description: 'A promotional HTML email template with a minimal design.',
        html: normalTemplate2,
        type: 'normal', // Add type
      },
      {
        id: 3,
        title: 'Normal Template 3',
        description: 'A promotional HTML email template with a minimal design.',
        html: normalTemplate3,
        type: 'normal', // Add type
      },
    ],
    ampscript: [
      {
        id: 1,
        title: 'AMPScript Template 1',
        description: 'A dynamic email template using AMPScript for personalized content.',
        html: ampscriptTemplate1,
        type: 'amp', // Add type
      },
      {
        id: 2,
        title: 'AMPScript Template 2',
        description: 'An AMPScript-powered email with conditional content blocks.',
        html: ampscriptTemplate2,
        type: 'amp', // Add type
      },
      {
        id: 3,
        title: 'AMPScript Template 3',
        description: 'An AMPScript-powered email with conditional content blocks.',
        html: ampscriptTemplate3,
        type: 'amp', // Add type
      },
    ],
    interactive: [
      {
        id: 1,
        title: 'Interactive Template 1',
        description: 'An interactive email template with hover effects and buttons.',
        html: interactiveTemplate1,
        type: 'interactive', // Add type
      },
      {
        id: 2,
        title: 'Interactive Template 2',
        description: 'A gamified email template with clickable elements.',
        html: interactiveTemplate2,
        type: 'interactive', // Add type
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Email Templates by Srihari Muppalla | Email Developer in Bangalore</title>
        <meta
          name="description"
          content="Explore professional email templates by Srihari Muppalla, an expert Email Developer in Bangalore, India. Specializing in responsive, high-converting email campaigns using HTML, CSS, JavaScript and AMPScript."
        />
        <meta
          name="keywords"
          content="Srihari Muppalla, Email Developer, email templates, Bangalore, India, responsive email campaigns, HTML, CSS, AMPScript, Salesforce, Litmus"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Srihari Muppalla" />
        <link rel="canonical" href="https://sriharim.in/email-templates" />

        {/* Open Graph Tags */}
        <meta
          property="og:title"
          content="Email Templates by Srihari Muppalla | Email Developer in Bangalore"
        />
        <meta
          property="og:description"
          content="Explore professional email templates by Srihari Muppalla, an expert Email Developer in Bangalore, India. Specializing in responsive, high-converting email campaigns using HTML, CSS, and AMPScript."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sriharim.in/email-templates" />
        <meta property="og:image" content="https://sriharim.in/Logo.png" />
        <meta property="og:image:alt" content="Email Templates by Srihari Muppalla" />
        <meta property="og:site_name" content="Srihari Muppalla Portfolio" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Email Templates by Srihari Muppalla | Email Developer in Bangalore"
        />
        <meta
          name="twitter:description"
          content="Explore professional email templates by Srihari Muppalla, an expert Email Developer in Bangalore, India. Specializing in responsive, high-converting email campaigns using HTML, CSS, and AMPScript."
        />
        <meta name="twitter:image" content="https://sriharim.in/Logo.png" />
        <meta name="twitter:image:alt" content="Email Templates by Srihari Muppalla" />
        <meta name="twitter:creator" content="@srihariM64" />
      </Helmet>
      <section className="emailtemplates-container">
        <h1 className="emailtemplates-title">Email Templates</h1>
        <p className="emailtemplates-subtitle">
          Explore my collection of HTML email templates, including normal, AMPScript-driven, and interactive designs, crafted to enhance email marketing campaigns and user engagement.
        </p>

        {/* Tabs */}
        <div className="emailtemplates-tabs">
          <button
            className={`emailtemplates-tab ${activeTab === 'normal' ? 'active' : ''}`}
            onClick={() => setActiveTab('normal')}
          >
            Normal Templates
          </button>
          <button
            className={`emailtemplates-tab ${activeTab === 'ampscript' ? 'active' : ''}`}
            onClick={() => setActiveTab('ampscript')}
          >
            AMPScript Templates
          </button>
          <button
            className={`emailtemplates-tab ${activeTab === 'interactive' ? 'active' : ''}`}
            onClick={() => setActiveTab('interactive')}
          >
            Interactive Templates
          </button>
        </div>

        {/* Template Content */}
        <div className="emailtemplates-content">
          {templates[activeTab].map((template) => (
            <TemplateItem
              key={template.id}
              template={template}
              onClick={() => setSelectedTemplate(template)}
            />
          ))}
        </div>

        {/* Preview Module */}
        {selectedTemplate && (
          <PreviewModule
            template={selectedTemplate}
            onClose={() => setSelectedTemplate(null)}
          />
        )}
      </section>
    </>
  );
};

export default EmailTemplates;